import { useState } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";
import { buildAuctionFaq, type FaqAuction } from "@/lib/auction-faq";
import { cn } from "@/lib/utils";

/**
 * Visible FAQ block for the auction detail page.
 *
 * Renders exactly the same question/answer pairs that the route emits as
 * FAQPage JSON-LD, so the structured data always matches on-page content.
 */
export function AuctionFaq({ auction }: { auction: FaqAuction }) {
  const items = buildAuctionFaq(auction);
  const [open, setOpen] = useState<number | null>(0);

  if (items.length === 0) return null;

  return (
    <section aria-labelledby="auction-faq-heading" className="mt-6">
      <h2
        id="auction-faq-heading"
        className="flex items-center gap-2 font-display text-lg text-gold-gradient"
      >
        <HelpCircle className="h-4 w-4 text-gold" aria-hidden /> Buying on BidNova — FAQ
      </h2>
      <div className="mt-3 divide-y divide-border/60 rounded-2xl border border-border/60 bg-surface-2/60 overflow-hidden">
        {items.map((item, idx) => {
          const isOpen = open === idx;
          return (
            <div key={item.q}>
              <h3>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={`auction-faq-panel-${idx}`}
                  id={`auction-faq-button-${idx}`}
                  onClick={() => setOpen(isOpen ? null : idx)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-medium transition-colors hover:text-gold"
                >
                  <span>{item.q}</span>
                  <ChevronDown
                    aria-hidden
                    className={cn(
                      "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200",
                      isOpen && "rotate-180 text-gold",
                    )}
                  />
                </button>
              </h3>
              <div
                id={`auction-faq-panel-${idx}`}
                role="region"
                aria-labelledby={`auction-faq-button-${idx}`}
                hidden={!isOpen}
                className="px-4 pb-4 -mt-1 text-sm leading-relaxed text-muted-foreground"
              >
                {item.a}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
